import React, { useState, useCallback, useEffect } from 'react';
import moment from 'moment';
import Filter from '@/pages/intelligence/components/Filter';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import useMarketFilterApi from '@/pages/intelligence/hooks/useMarketSelector';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import { trankMarketsParamsExceptGlobal } from '@/pages/intelligence/utils/commonUtils';
import {
  updateInputListRegionOptions,
  handleExtendTimeFilterValue,
  getFilterExtendDefaultValue,
  updateInputListRangeTime,
} from '@intelligence/utils/filterUtils';
import { useUpdateFilterFormValues } from '@intelligence/hooks/useUpdateFilterFormValues';
import { getPlatformDeviceCascaderOptions } from '@/pages/intelligence/pages/TopCharts/PCGames/_config/utils';

interface IUserEngagementAmpereFilterProps {
  isTopCompanies?: boolean;
  timeFilterValues?: any;
  filterConfirm: (value: any) => void;
  filterReset: () => void;
  appIds: string;
  entityType: string;
  basicInfo: any;
  globalOnly?: boolean;
  hasDevice?: boolean;
  onFilterInit: (params: { loading: boolean; regionOptions: any }) => void;
  sinceReleaseTime: string;
  earliestDataTime: string;
  deviceDefVales: string[];
  deviceOptions: any;
  currentMetricDateType: string;
  getRadioOptionsTips?: Function;
}

export default function UserEngagementAmpereFilter(props: IUserEngagementAmpereFilterProps) {
  const {
    isTopCompanies = false,
    timeFilterValues,
    filterConfirm,
    // filterReset,
    // appIds,
    entityType,
    // basicInfo,
    globalOnly,
    hasDevice,
    onFilterInit,
    sinceReleaseTime,
    earliestDataTime,
    deviceDefVales,
    deviceOptions = [],
    currentMetricDateType,
    getRadioOptionsTips,
  } = props;

  const defaultValue = {
    [filterKeyMap.REGION]: {
      regions: [],
      markets: ['global'],
      type: 'market',
    },
    [filterKeyMap.RANGE_TIME]: {
      type: 'date',
      value: ['', ''],
    },
    [filterKeyMap.MAU_DEVICE]: { value: deviceDefVales },
  };

  // console.log('UserEngagementAmpereFilter',props);

  // 继承逻辑在这写
  const { isOperateDetail, detailFilterExtend, setDetailFilterExtend } = useFilterExtend({});
  const extendDefaultFilterValue = getFilterExtendDefaultValue(
    isOperateDetail,
    detailFilterExtend,
    defaultValue,
  );
  const [extendDefaultValue] = useState(
    isOperateDetail
      ? {
          [filterKeyMap.RANGE_TIME]: {
            type: 'month',
            value: handleExtendTimeFilterValue({
              // 仅有月粒度，单选 1 个月
              onlyMonth: true,
              extendFilterValues: detailFilterExtend[filterKeyMap.RANGE_TIME],
              defaultValue: defaultValue[filterKeyMap.RANGE_TIME]?.value,
            }),
          },
          [filterKeyMap.REGION]: extendDefaultFilterValue?.[filterKeyMap.REGION],
          // 不参与继承
          [filterKeyMap.MAU_DEVICE]: {
            value: defaultValue[filterKeyMap.MAU_DEVICE]?.value,
          },
        }
      : defaultValue,
  );

  const [formValue, setFormValue] = useState(extendDefaultValue);

  const defaultInputList = {
    RANGE_TIME: {
      type: filterKeyMap.RANGE_TIME,
      key: filterKeyMap.RANGE_TIME,
      showSelectType: false,
      calendarType: 'month',
      earliestDataTime,
      sinceReleaseTime,
      isShowAlloftime: false,
      default: [
        moment().subtract(6, 'month').startOf('month'),
        moment().subtract(1, 'month').endOf('month'),
      ],
    },
    REGION: {
      key: filterKeyMap.REGION,
      default: defaultValue[filterKeyMap.REGION],
      visible: 1,
      // Ampere 子段固有可选 'Global except China mainland'
      disableNoChina: false,
      // "popularList": [
      //   "br",
      //   "mx",
      //   "ru",
      //   "tr",
      //   "us"
      // ],
      options: [],
      isTopCompanies,
      entityType,
      showRadioBubble: true,
    },
    MAU_DEVICE: {
      key: filterKeyMap.MAU_DEVICE,
      type: filterKeyMap.CONSOLE_DEVICE,
      options: deviceOptions,
      default: defaultValue[filterKeyMap.MAU_DEVICE],
      useCascaderStyle: true,
      cascaderHierarchicalOptions: getPlatformDeviceCascaderOptions(deviceOptions),
    },
  };

  const updateInputList = (globalOnly: boolean) => {
    const list = [];
    list.push(defaultInputList.RANGE_TIME);
    if (!globalOnly) {
      list.push(defaultInputList.REGION);
    }
    if (hasDevice) {
      list.push(defaultInputList.MAU_DEVICE);
    }
    return list;
  };

  const [inputList, setInputList] = useState(updateInputList(globalOnly));

  useEffect(() => {
    /** 更新时间筛选器快捷键 */
    if (inputList) {
      const updateOptionInput = updateInputListRangeTime({
        inputList,
        key: filterKeyMap.RANGE_TIME,
        earliestDataTime,
        sinceReleaseTime,
      });
      setInputList(updateOptionInput);
    }
  }, [earliestDataTime, sinceReleaseTime]);

  // 根据当前指标粒度指定当前时间筛选器粒度
  useEffect(() => {
    setInputList((prev) => {
      const rangeTime = prev?.find((item) => item.key === filterKeyMap.RANGE_TIME);

      rangeTime && (rangeTime.calendarType = currentMetricDateType);

      return prev;
    });
  }, [currentMetricDateType]);

  /** 优化 TODO：这一整块及 inputList 里的国家参数都放到 model 里 */
  const { filterLoading, regionOptions } = useMarketFilterApi({});

  useEffect(() => {
    if (onFilterInit instanceof Function) {
      onFilterInit({
        loading: filterLoading,
        regionOptions,
      });
    }
  }, [filterLoading, regionOptions]);

  useEffect(() => {
    /** 国家筛选器的列表值 */
    let newInputList = updateInputList(globalOnly);
    newInputList = updateInputListRegionOptions({
      inputList: newInputList,
      regionOptions: !Array.isArray(regionOptions) ? [] : regionOptions,
      getRadioOptionsTips,
      regionKey: filterKeyMap.REGION,
    });
    setInputList(newInputList);
  }, [globalOnly, regionOptions, getRadioOptionsTips]);

  /** 时间筛选器值由父组件更新 */
  const combinedValues = useUpdateFilterFormValues({
    formValue,
    timeFilterValues,
  });

  useEffect(() => {
    setFormValue(combinedValues);
  }, [JSON.stringify(combinedValues)]);

  const onConfirm = useCallback(
    (value, apiValue) => {
      setFormValue(value);
      /** 修改筛选器值转换为接口参数 */
      // 只有 global 和 globalexeptchina 是 region，其他发 market
      const rpdFilterValues = trankMarketsParamsExceptGlobal({
        regionType: apiValue[filterKeyMap.REGION]?.region_type,
        regions: apiValue[filterKeyMap.REGION]?.regions,
        markets: apiValue[filterKeyMap.REGION]?.markets,
      });
      const filterForm: any = {
        region_type: rpdFilterValues.regionType,
        regions: rpdFilterValues.regions,
        markets: rpdFilterValues.regions,
        date_type: apiValue[filterKeyMap.RANGE_TIME]?.type,
        start_date: apiValue[filterKeyMap.RANGE_TIME]?.start_date,
        end_date: apiValue[filterKeyMap.RANGE_TIME]?.end_date,
      };
      if (hasDevice) {
        filterForm.device = value[filterKeyMap.MAU_DEVICE]?.value;
      }
      filterConfirm(filterForm);
      setDetailFilterExtend(apiValue);
    },
    [filterConfirm],
  );

  return (
    <Filter
      value={formValue}
      inputList={inputList}
      // onInit={onInit}
      onConfirm={onConfirm}
      onReset={() => {}}
      showButton={false}
      defaultValue={defaultValue}
    />
  );
}
